/**
 * Empty-category guard.
 *
 * Some categories map to product types where every item is out of stock
 * (Area Rugs was 19 products / 0 in stock). Listing queries correctly filter
 * `available_for_sale:true`, so those categories resolve to an empty grid — a
 * dead end when they are advertised in the nav.
 *
 * The in-stock count is derived from the products actually fetched for a
 * category (recorded by the category page), NOT from `fetchProductCount`:
 * that runs on the Shopify `search` connection, which returns 0 for
 * `available_for_sale` filters and would wrongly mark every category empty.
 *
 * Known-empty categories are hidden from nav / tiles / the categories index,
 * but the category page itself keeps working (honest empty state + escape
 * links) and the sitemap is untouched.
 */

import { useEffect, useState } from "react";
import { CATEGORY_STOCK, EMPTY_CATEGORY_SLUGS } from "@/data/categoryStock";

const STORAGE_KEY = "loftloom_category_stock_v2";

/**
 * Categories measured at exactly zero in-stock products at build time by
 * scripts/generate-category-stock.mjs (no hand-kept list — stock returning
 * un-hides the category on the next build). Build data wins over any stale
 * value persisted in the browser.
 */
const SEED_EMPTY: string[] = EMPTY_CATEGORY_SLUGS;

type StockMap = Record<string, number>;

let stock: StockMap = load();
const listeners = new Set<() => void>();

function load(): StockMap {
  const base: StockMap = {};
  for (const slug of SEED_EMPTY) base[slug] = 0;
  if (typeof localStorage === "undefined") return base;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as StockMap;
      // Build-measured zeros are re-applied last so a stale positive value in
      // localStorage cannot re-advertise an empty category.
      if (parsed && typeof parsed === "object") return { ...base, ...parsed, ...base };
    }
  } catch {
    // ignore
  }
  return base;
}

function persist() {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stock));
  } catch {
    // ignore
  }
}

/** Record how many in-stock products a category actually returned. */
export function recordCategoryStock(slug: string, inStockCount: number) {
  if (!slug) return;
  if (stock[slug] === inStockCount) return;
  stock = { ...stock, [slug]: inStockCount };
  persist();
  listeners.forEach((fn) => fn());
}

/** True only when we have observed the category returning zero products. */
export function isCategoryKnownEmpty(slug: string): boolean {
  return stock[slug] === 0;
}

/** Extract a category slug from an app path, if it is a category link. */
export function categorySlugFromHref(href: string): string | null {
  const match = /^\/category\/([^/?#]+)/.exec(href);
  return match ? match[1] : null;
}

/** True when a link points at a category we know resolves to nothing. */
export function isEmptyCategoryHref(href: string): boolean {
  const slug = categorySlugFromHref(href);
  return slug ? isCategoryKnownEmpty(slug) : false;
}

/** Drop links that point at known-empty categories. */
export function withoutEmptyCategories<T extends { href: string }>(items: T[]): T[] {
  return items.filter((item) => !isEmptyCategoryHref(item.href));
}

/**
 * Menus only link categories with at least this many in-stock products. A menu
 * entry promises a range: "Rocking Chairs" and "Outdoor Sofas" each opened on a
 * single product.
 */
export const NAV_MIN_STOCK = 8;

/**
 * True when a link points at a category the build measured below NAV_MIN_STOCK
 * (or that is known empty). Unmeasured categories (price bands, style edits,
 * anything not in CATEGORY_STOCK) are never thin. For the header menus only:
 * the page, its sitemap entry and breadcrumbs to it all stay.
 */
export function isThinCategoryHref(href: string): boolean {
  const slug = categorySlugFromHref(href);
  if (!slug) return false;
  if (isCategoryKnownEmpty(slug)) return true;
  const built = CATEGORY_STOCK[slug];
  return typeof built === "number" && built >= 0 && built < NAV_MIN_STOCK;
}

/** Drop menu links that point at thin or empty categories. */
export function withoutThinCategories<T extends { href: string }>(items: T[]): T[] {
  return items.filter((item) => !isThinCategoryHref(item.href));
}

/**
 * Subscribe to the known-empty set so nav components re-render once a
 * category has been measured.
 */
export function useCategoryStockVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const fn = () => setVersion((v) => v + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return version;
}
